/**
=========================================================
* Soft UI Dashboard React - v4.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";

// Soft UI Dashboard React components
import SoftBox from "components/MDBox";
import SoftTypography from "components/MDTypography";
import AsnStatusController from "Controller/AsnStatusController";

// Soft UI Dashboard React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import DataTable from "examples/Tables/DataTable";
import { DataGrid } from "@mui/x-data-grid";
import { useTheme } from "@mui/material/styles";
import {
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Box,
  TextField,
  Chip,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import {
  CalendarMonth,
  Close,
  ChevronLeft,
  ChevronRight,
  EventAvailable,
  FactCheck,
  Inventory2,
  ReceiptLong,
} from "@mui/icons-material";

function ASNStatus() {
  const {
    asnStatus,
    generateRows,
    columns,
    materialcolumns,
    generateMaterialRows,
    cloading,
    confValue,
    handleVendorChange,
    selectedParty,
    handleSelectionChange,
    paginationModel,
    setPaginationModel,
    rowCount,
    openPopup,
    handleClosePopup,
    selectedData,
    openAppointmentPopup,
    openBookingPreviewPopup,
    openAppointmentListPopup,
    selectedAppointmentData,
    selectedAppointmentListData,
    handleCloseAppointmentPopup,
    handleCloseAppointmentListPopup,
    handleCloseBookingPreviewPopup,
    selectedCalendarDate,
    handleAppointmentDateSelect,
    selectedEwayBillFile,
    handleEwayBillFileChange,
    currentCalendarMonth,
    handleCalendarMonthChange,
    getCalendarDays,
    getMonthLabel,
    isPastDate,
    isCurrentOrFutureMonth,
    appointmentCalendarData,
    appointmentDateSlotMap,
    getSlotStatus,
    isSlotDateAvailable,
    approvedMaterialColumns,
    appointmentListColumns,
    approvedAsnRows,
    bookingPreviewLoading,
    getSelectedApprovedAsnQuantity,
    formatDateValue,
    bookingSelectionConfirmed,
    confirmedBookingQuantity,
    handleBookingPreviewNext,
    handleAppointmentSubmit,
    editData,
    openEditPopup,
    handleEditClick,
    handleCloseEditPopup,
    editColumns,
    uploadBillDetails,
    searchPONumber,
    setSearchPONumber,
    buildDocumentLinks,

    openDeletePopup,
    deleteData,
    handleCloseDeletePopup,
    handleDeleteASN,
  } = AsnStatusController();
  const rowsLength = generateRows(asnStatus).length;
  const rows = generateRows(asnStatus);
  const materialrows = generateMaterialRows(asnStatus);
  const materialTotalQty = materialrows.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
  const appointmentTotalQty = (selectedAppointmentListData?.rows || []).reduce(
    (sum, item) => sum + Number(item.quantity || 0),
    0
  );

  const theme = useTheme();

  const customStyles = {
    option: (provided) => ({
      ...provided,
      zIndex: 999, // Adjust the value as needed
      fontSize: "0.9rem",
    }),
    menu: (provided) => ({
      ...provided,
      zIndex: 1000, // Make sure this value is higher than the table header's z-index
      fontSize: "0.9rem",
    }),
  };

  const calendarDays = getCalendarDays(currentCalendarMonth);
  const selectedCalendarDetails = appointmentCalendarData[selectedCalendarDate];
  const selectedDateSlotDetails = appointmentDateSlotMap[selectedCalendarDate];
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const selectedDocuments =
    selectedData?.poNumber && selectedData?.vendorCode
      ? buildDocumentLinks(selectedData.poNumber, selectedData.vendorCode, selectedData)
      : [];
  const getDocumentIcon = (iconName) => {
    switch (iconName) {
      case "fact_check":
        return <FactCheck fontSize="small" />;
      case "inventory":
        return <Inventory2 fontSize="small" />;
      case "receipt":
        return <ReceiptLong fontSize="small" />;
      default:
        return <ReceiptLong fontSize="small" />;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* {cloading && <NewLoader></NewLoader>} */}
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox
              pt={2}
              px={2}
              mb={3}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
            >
              {/* Page Title */}
              <SoftTypography variant="h6" fontWeight="medium">
                ASN Status
              </SoftTypography>

              {/* PO Filter */}
              <TextField
                label="PO Number"
                variant="outlined"
                size="small"
                placeholder="Search PO Number"
                value={searchPONumber}
                onChange={(e) => setSearchPONumber(e.target.value)}
                sx={{ width: 250 }}
              />
            </SoftBox>

            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
              p={1}
            >
              <MDBox pt={3} pb={2} px={2} sx={{ height: "50%", width: "100%" }}>
                {cloading ? (
                  <MDBox display="flex" justifyContent="center" alignItems="center" height={300}>
                    <CircularProgress color="error" />
                  </MDBox>
                ) : rows.length > 0 ? (
                  <DataGrid
                    rows={rows}
                    columns={columns}
                    disableSelectionOnClick
                    pagination
                    paginationMode="server"
                    // sortingMode="server"
                    // filterMode="server"
                    rowCount={rowCount}
                    paginationModel={paginationModel} // Use paginationModel directly
                    onPaginationModelChange={setPaginationModel} // Use this instead of separate handlers
                    pageSizeOptions={[100]} // Add page size options
                    autoHeight
                    sx={{
                      flex: 1,
                      "& .MuiDataGrid-columnHeaders": {
                        fontWeight: "bold",
                        textAlign: "center",
                      },
                      "& .MuiDataGrid-cell": {
                        borderBottom: "1px solid #f0f0f0",
                        textAlign: "center",
                      },
                    }}
                    sortingOrder={["asc", "desc"]}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 100,
                        },
                      },
                      sorting: {
                        sortModel: [{ field: "poDate", sort: "desc" }],
                      },
                    }}
                  />
                ) : (
                  <MDBox display="flex" justifyContent="center" alignItems="center" height={300}>
                    <MDTypography variant="caption" fontWeight="bold" sx={{ fontSize: "1rem" }}>
                      No data available!
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Dialog
        open={openPopup}
        onClose={handleClosePopup}
        fullWidth
        maxWidth={false} // disable maxWidth limit
        PaperProps={{
          sx: {
            width: "80vw", // or '1200px'
            maxWidth: "none", // prevent theme override
          },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          Material Details
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "normal", fontSize: "1rem", color: "black" }}
            >
              {" "}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "normal", fontSize: "1rem", color: "black", textAlign: "right" }}
            >
              Total Qty: {materialTotalQty}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            "& th": {
              whiteSpace: "nowrap", // prevent line breaks
              fontSize: "0.875rem", // optional: reduce font size
            },
          }}
        >
          {materialrows.length > 0 ? (
            <DataTable
              table={{ columns: materialcolumns, rows: materialrows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              pagination={false}
              noEndBorder
            />
          ) : (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={140}>
              <MDTypography variant="button" fontWeight="medium">
                No box numbers found with appointment taken = yes.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>
          <Button
            onClick={handleClosePopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAppointmentListPopup}
        onClose={handleCloseAppointmentListPopup}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "96vw", md: "88vw", lg: "84vw" },
            maxWidth: "1200px",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          Appointment Data
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: "normal", fontSize: "1rem" }}>
              PO Number: {selectedAppointmentListData?.poNumber || "-"}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: "normal", fontSize: "1rem", textAlign: "right" }}
            >
              Total Qty: {appointmentTotalQty}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            overflowX: "hidden",
            pl: { xs: 1.5, md: 3 },
            pr: { xs: 1.5, md: 2 },
            "& th": {
              whiteSpace: "nowrap",
              wordBreak: "normal",
              overflowWrap: "normal",
              fontSize: "2.4rem",
              fontWeight: 700,
              textAlign: "left",
              px: 1,
              py: 1,
            },
            "& td": {
              whiteSpace: "normal",
              wordBreak: "break-word",
              overflowWrap: "break-word",
              verticalAlign: "top",
              px: 1,
              py: 0.9,
              minWidth: 0,
              fontSize: "2rem",
            },
            "& td > div": {
              verticalAlign: "top",
            },
          }}
        >
          {selectedAppointmentListData?.rows?.length > 0 ? (
            <MDBox sx={{ ml: { xs: 0.5, md: 1.5 } }}>
              <DataTable
                table={{ columns: appointmentListColumns, rows: selectedAppointmentListData.rows }}
                isSorted={false}
                entriesPerPage={false}
                showTotalEntries={false}
                pagination={false}
                noEndBorder
              />
            </MDBox>
          ) : (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={140}>
              <MDTypography variant="button" fontWeight="medium">
                No appointment data available.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>
          <Button
            onClick={handleCloseAppointmentListPopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openAppointmentPopup}
        onClose={handleCloseAppointmentPopup}
        fullWidth
        maxWidth={false}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: "96vw", md: "88vw", lg: "78vw" },
            maxWidth: "1180px",
            borderRadius: "26px",
            overflow: "hidden",
            maxHeight: "96vh",
            m: "auto",
            bgcolor: "background.paper",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <EventAvailable color="action" />
                <Typography variant="h6" fontWeight="bold">
                  Appointment Calendar
                </Typography>
                <Chip
                  label={`PO: ${selectedAppointmentData?.poNumber || "-"}`}
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 700,
                    fontSize: "1rem",
                    height: 36,
                    "& .MuiChip-label": {
                      px: 1.5,
                    },
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleCloseAppointmentPopup}
                sx={{ color: "text.secondary", mt: -0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Select an available date to open confirm booking and then submit from here.
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2, mt: 2, overflow: "hidden" }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
              gap: 3,
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "22px",
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                  py: 1.5,
                  bgcolor: "background.default",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <IconButton
                  onClick={() => handleCalendarMonthChange(-1)}
                  disabled={!isCurrentOrFutureMonth(new Date(currentCalendarMonth.getFullYear(), currentCalendarMonth.getMonth() - 1, 1))}
                >
                  <ChevronLeft />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonth color="action" />
                  <Typography variant="h6" fontWeight="bold">
                    {getMonthLabel(currentCalendarMonth)}
                  </Typography>
                </Box>
                <IconButton onClick={() => handleCalendarMonthChange(1)}>
                  <ChevronRight />
                </IconButton>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  {weekDays.map((day) => (
                    <Box
                      key={day}
                      sx={{
                        textAlign: "center",
                        py: 0.5,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "text.secondary",
                      }}
                    >
                      {day}
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.5,
                  }}
                >
                  {calendarDays.map((day, index) => {
                    if (!day) {
                      return <Box key={`blank-${index}`} sx={{ minHeight: 58 }} />;
                    }

                    const isSelected = selectedCalendarDate === day.dateKey;
                    const slotStatus = getSlotStatus(day.dateKey);
                    const isDisabled = !isSlotDateAvailable(day.dateKey);
                    const statusColor =
                      slotStatus === "Available"
                        ? "success.main"
                        : slotStatus === "Fully Booked"
                          ? "warning.main"
                          : "text.disabled";

                    return (
                      <Box
                        key={day.dateKey}
                        onClick={() => !isDisabled && handleAppointmentDateSelect(day.dateKey)}
                        sx={{
                          minHeight: 58,
                          p: 0.5,
                          borderRadius: "12px",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.45 : 1,
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected
                            ? "text.primary"
                            : slotStatus === "Available"
                              ? "success.main"
                              : slotStatus === "Fully Booked"
                                ? "warning.main"
                                : "divider",
                          bgcolor: isSelected
                            ? "action.selected"
                            : slotStatus === "Available"
                              ? "#f3fbf4"
                              : slotStatus === "Fully Booked"
                                ? "#fff7e6"
                                : "background.paper",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {day.day}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.35,
                            color: statusColor,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            fontSize: "0.62rem",
                          }}
                        >
                          {slotStatus}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>

            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "22px",
                p: 2.5,
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="bold" sx={{ mb: 0.5 }}>
                  Booking Form
                </Typography>
                {/* <Typography variant="body2" sx={{ color: "#67748e", mb: 2.5 }}>
                  {selectedCalendarDate
                    ? `Selected Date: ${formatDateValue(selectedCalendarDate)}`
                    : "Aaj ya future date select kijiye"}
                </Typography> */}

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "16px",
                    bgcolor: "background.default",
                    border: 1,
                    borderColor: "divider",
                    mb: 2,
                  }}
                >
                  <Typography variant="button" fontWeight="bold" sx={{ display: "block", mb: 1 }}>
                    Upload E-Way Bill PDF (Optional)
                  </Typography>
                  <TextField
                    type="file"
                    fullWidth
                    inputProps={{ accept: ".pdf,application/pdf" }}
                    onChange={(e) => handleEwayBillFileChange(e.target.files?.[0] || null)}
                    helperText={selectedEwayBillFile?.name || "Only PDF file allowed."}
                  />
                </Box>

                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: "16px",
                    bgcolor: "background.default",
                    border: 1,
                    borderColor: "divider",
                    mb: 2,
                  }}
                >
                  <Typography variant="button" fontWeight="bold">
                    Total Qty Available On Selected Date
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" sx={{ mt: 0.5 }}>
                    {selectedDateSlotDetails?.quantityPercentExtra || 0}
                  </Typography>
                </Box>

                <Box sx={{ display: "grid", gap: 1, mb: 2 }}>
                  <Chip
                    label={`Selected Quantity: ${bookingSelectionConfirmed ? confirmedBookingQuantity : 0}`}
                    variant="outlined"
                    sx={{
                      justifyContent: "flex-start",
                      bgcolor: "background.default",
                    }}
                  />
                </Box>

                {selectedCalendarDetails ? (
                  <Box sx={{ display: "grid", gap: 1 }}>
                    {selectedCalendarDetails.bookings.slice(0, 4).map((booking, index) => (
                      <Box
                        key={`${booking.poNumber}-${booking.boxNumber}-${index}`}
                        sx={{
                          p: 1.35,
                          borderRadius: "16px",
                          border: 1,
                          borderColor: "divider",
                          bgcolor: "background.default",
                        }}
                      >
                        <Typography variant="button" fontWeight="bold">
                          PO: {booking.poNumber}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary", mt: 0.5 }}
                        >
                          Box: {booking.boxNumber || "-"} | Qty: {booking.quantity || 0}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ display: "block", color: "text.secondary", mt: 0.25 }}
                        >
                          City: {booking.city || "-"} | Material: {booking.materialCode || "-"}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Box sx={{ minHeight: 120 }} />
                )}
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5, justifyContent: "flex-end" }}>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleAppointmentSubmit}
            disabled={!selectedCalendarDate || !bookingSelectionConfirmed}
          >
            Submit
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openBookingPreviewPopup}
        onClose={handleCloseBookingPreviewPopup}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "98vw", md: "90vw", lg: "82vw" },
            maxWidth: "1280px",
            borderRadius: "24px",
            maxHeight: "92vh",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              justifyContent: "space-between",
              gap: 1.5,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Typography variant="h6" fontWeight="bold">
              Confirm Booking
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", md: "flex-end" },
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                label={`Date: ${selectedCalendarDate ? formatDateValue(selectedCalendarDate) : "-"}`}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  height: 34,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
              <Chip
                label={`PO: ${selectedAppointmentData?.poNumber || "-"}`}
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  height: 34,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />

              <Chip
                label={`Available Qty: ${selectedDateSlotDetails?.quantityPercentExtra || 0}`}
                color="warning"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  height: 34,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
              <Chip
                label={`Selected Qty: ${getSelectedApprovedAsnQuantity()}`}
                color="success"
                variant="outlined"
                sx={{
                  fontWeight: 700,
                  height: 34,
                  "& .MuiChip-label": {
                    px: 1.5,
                  },
                }}
              />
            </Box>
          </Box>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            Select booking quantity for the appointed date.
          </Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            mt: 2,
            "& .MuiTable-root": {
              tableLayout: "fixed",
              width: "100%",
            },
            "& th": {
              whiteSpace: "nowrap",
              fontSize: "0.78rem",
              px: 0.75,
              py: 0.85,
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
            "& td": {
              px: 0.50,
              py: 0.65,
              overflow: "hidden",
            },
            "& td > div": {
              display: "block",
              width: "100%",
              maxWidth: "100%",
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            },
          }}
        >
          {bookingPreviewLoading ? (
            <MDBox display="flex" justifyContent="center" alignItems="center" height={220}>
              <MDTypography variant="button" fontWeight="medium">
                Loading approved ASN details...
              </MDTypography>
            </MDBox>
          ) : approvedAsnRows.length > 0 ? (
            <DataTable
              table={{ columns: approvedMaterialColumns, rows: approvedAsnRows }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              pagination={false}
              noEndBorder
            />
          ) : (
            <MDBox display="flex" justifyContent="center" alignItems="center" height={220}>
              <MDTypography variant="button" fontWeight="medium">
                No approved ASN details available.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>

          <Button
            onClick={handleCloseBookingPreviewPopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
          <MDButton
            variant="gradient"
            color="error"
            onClick={handleBookingPreviewNext}
            disabled={!getSelectedApprovedAsnQuantity()}
          >
            Next
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Edit dialogue */}
      <Dialog
        open={openEditPopup}
        onClose={handleCloseEditPopup}
        fullWidth
        maxWidth={false} // disable maxWidth limit
        PaperProps={{
          sx: {
            width: "80vw", // or '1200px'
            maxWidth: "none", // prevent theme override
          },
          onClick: (e) => e.stopPropagation(),
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: "bold",
            fontSize: "1.5rem",
            display: "flex",
            flexDirection: "column",
            gap: 0.5,
            pb: 0,
          }}
        >
          Add Bill Details
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: "normal", fontSize: "1rem", color: "black" }}
          ></Typography>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            "& th": {
              whiteSpace: "nowrap", // prevent line breaks
              fontSize: "0.875rem", // optional: reduce font size
            },
          }}
        >
          {editData.length > 0 ? (
            <DataTable
              table={{ columns: editColumns, rows: editData }}
              isSorted={false}
              entriesPerPage={false}
              showTotalEntries={false}
              noEndBorder
            />
          ) : (
            <MDBox display="flex" justifyContent="center" alignItems="center" minHeight={140}>
              <MDTypography variant="button" fontWeight="medium">
                No editable box numbers found with appointment taken = yes.
              </MDTypography>
            </MDBox>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "end", px: 3, py: 2 }}>
          <MDButton variant="gradient" color="error" onClick={uploadBillDetails}>
            Submit
          </MDButton>
          <Button
            onClick={handleCloseEditPopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeletePopup} onClose={handleCloseDeletePopup}>
        <DialogTitle>Delete ASN</DialogTitle>

        <DialogContent>
          <Typography>
            Are you sure you want to delete this ASN for PO Number:{" "}
            <b>{deleteData?.poNumber}</b>?
          </Typography>

          <Typography  sx={{ fontSize: "0.95rem", mt: 1 }}>
            <strong>Note:</strong> Any appointments, invoices and documents associated with this ASN will also be cancelled.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDeletePopup}>
            Cancel
          </Button>

          <MDButton
            variant="gradient"
            color="error"
            onClick={handleDeleteASN}
          >
            Delete
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default ASNStatus;
